import React from 'react';
import { Link } from 'react-router-dom';

const coreValues = [
  'Customer-first service and transparent estimates',
  'Technical excellence across today\u2019s common printer systems',
  'Respect for your time, office workflow, and urgency',
  'Responsible, eco-friendly practices where possible',
];

const overviewServices = [
  'Printer repair for common issues and error codes',
  'Preventative maintenance to extend the life of your equipment',
  'Sales of new and refurbished printers',
  'Ink/toner supplies and replacement consumables',
  'Network setup and troubleshooting (Wi-Fi/Ethernet)',
];

const features = [
  {
    icon: '🛠️',
    title: 'Expert technicians',
    description: 'Skilled support for popular brands like Canon and more — diagnosing problems accurately before recommending solutions.',
  },
  {
    icon: '⚡',
    title: 'Rapid response',
    description: 'We understand downtime impacts your day. We prioritize urgent printer issues and keep you informed at each step.',
  },
  {
    icon: '✅',
    title: 'Quality you can count on',
    description: 'Repairs are performed with care and focus on long-term reliability, not quick fixes.',
  },
  {
    icon: '🤝',
    title: 'Satisfaction promise',
    description: 'If the repair doesn\u2019t meet expectations, we work with you until it does.',
  },
  {
    icon: '📍',
    title: 'Local & convenient',
    description: 'Proudly serving Purnea with practical scheduling and easy support options.',
  },
  {
    icon: '🌱',
    title: 'Sustainable practices',
    description: 'We aim to extend the life of your equipment and reduce waste through repair and maintenance where it makes sense.',
  },
];

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-5">
            15 years in Purnea
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-5">
            Your trusted partner for printer services & repair
          </h1>
          <p className="text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
            At <span className="font-medium text-gray-700">MV Computers</span>, we're committed to keeping your printers running reliably — whether you need quick repair, preventive maintenance, or genuine supplies.
          </p>
        </div>
      </section>

      {/* Mission & values */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Our story</p>
        <h2 className="text-2xl font-bold text-gray-900 mb-5">Mission & story</h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          Our mission is simple: reduce downtime and help customers print with confidence. Every call deserves honest diagnostics, clear communication, and practical solutions that fit your budget.
        </p>
        <p className="text-sm text-gray-600 leading-relaxed mb-8">
          MV Computers was built from a passion for fixing what's broken and improving what works. Over time, we expanded to cover repair, maintenance, network setup, and printer supply support — so customers can rely on one local team for complete printer care.
        </p>

        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Core values</h3>
          <ul className="flex flex-col gap-3">
            {coreValues.map((value) => (
              <li key={value} className="flex items-start gap-2.5 text-sm text-gray-600">
                <span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-bold">✓</span>
                {value}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-white border-y border-gray-100 py-16">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">What we offer</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Services overview</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            We provide printer support for both personal and business needs. Here's a quick overview — detailed services are on our dedicated services page.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {overviewServices.map((svc) => (
              <div key={svc} className="flex items-start gap-2.5 text-sm text-gray-700 bg-gray-50 rounded-lg px-3.5 py-3">
                <span className="mt-0.5 text-blue-500">•</span>
                {svc}
              </div>
            ))}
          </div>
          <Link to="/services" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors">
            View all services →
          </Link>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Why MV Computers</p>
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Why choose us</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
              <span className="text-2xl mb-3 block">{f.icon}</span>
              <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Need printer assistance?</h2>
          <p className="text-blue-200 text-sm mb-7 leading-relaxed">
            Don't let printing problems slow you down. Contact MV Computers for a consultation, a repair request, or help choosing the right solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5">
            <a
              href="/contact"
              className="bg-white text-blue-700 font-semibold text-sm px-8 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              Get in touch
            </a>
            <a
              href="/book-service"
              className="bg-blue-700 text-white font-semibold text-sm px-8 py-3 rounded-xl border border-blue-400 hover:bg-blue-800 transition"
            >
              Book a service
            </a>
          </div>
          <p className="text-xs text-blue-200">
            Prefer supplies or upgrades?{' '}
            <a href="/products" className="underline hover:text-white transition">
              Explore products & supplies
            </a>
          </p>
        </div>
      </section>

    </div>
  );
};

export default About;